import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { Book, Chapter, BookSection } from '../types/book';
import { SearchResult } from '../types/search';
import { bookService } from '../services/book';

interface BookState {
  book: Book | null;
  currentChapter: Chapter | null;
  currentSection: BookSection | null;
  isLoading: boolean;
  error: string | null;
  expandedChapters: Set<string>;
  expandedSections: Set<string>;
  searchQuery: string;
  searchResults: SearchResult[];
  isSearching: boolean;
}

interface BookContextType extends BookState {
  loadBook: () => Promise<void>;
  setCurrentChapter: (chapterId: string) => void;
  setCurrentSection: (sectionId: string) => void;
  toggleChapterExpansion: (chapterId: string) => void;
  toggleSectionExpansion: (sectionId: string) => void;
  clearError: () => void;
}

const BookContext = createContext<BookContextType | undefined>(undefined);

type BookAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; payload: Book }
  | { type: 'LOAD_FAILURE'; payload: string }
  | { type: 'SET_CURRENT_CHAPTER'; payload: Chapter }
  | { type: 'SET_CURRENT_SECTION'; payload: BookSection }
  | { type: 'TOGGLE_CHAPTER_EXPANSION'; payload: string }
  | { type: 'TOGGLE_SECTION_EXPANSION'; payload: string }
  | { type: 'CLEAR_ERROR' };

const initialState: BookState = {
  book: null,
  currentChapter: null,
  currentSection: null,
  isLoading: false,
  error: null,
  expandedChapters: new Set(),
  expandedSections: new Set(),
  searchQuery: '',
  searchResults: [],
  isSearching: false,
};

function bookReducer(state: BookState, action: BookAction): BookState {
  switch (action.type) {
    case 'LOAD_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOAD_SUCCESS':
      return {
        ...state,
        book: action.payload,
        isLoading: false,
        error: null,
      };
    case 'LOAD_FAILURE':
      return {
        ...state,
        book: null,
        isLoading: false,
        error: action.payload,
      };
    case 'SET_CURRENT_CHAPTER':
      return {
        ...state,
        currentChapter: action.payload,
        currentSection: null,
      };
    case 'SET_CURRENT_SECTION':
      return {
        ...state,
        currentSection: action.payload,
      };
    case 'TOGGLE_CHAPTER_EXPANSION': {
      const newExpanded = new Set(state.expandedChapters);
      if (newExpanded.has(action.payload)) {
        newExpanded.delete(action.payload);
      } else {
        newExpanded.add(action.payload);
      }
      return {
        ...state,
        expandedChapters: newExpanded,
      };
    }
    case 'TOGGLE_SECTION_EXPANSION': {
      const newExpanded = new Set(state.expandedSections);
      if (newExpanded.has(action.payload)) {
        newExpanded.delete(action.payload);
      } else {
        newExpanded.add(action.payload);
      }
      return {
        ...state,
        expandedSections: newExpanded,
      };
    }
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

interface BookProviderProps {
  children: ReactNode;
}

export function BookProvider({ children }: BookProviderProps) {
  const [state, dispatch] = useReducer(bookReducer, initialState);

  const loadBook = async () => {
    dispatch({ type: 'LOAD_START' });
    try {
      const book = await bookService.getBook();
      dispatch({ type: 'LOAD_SUCCESS', payload: book });
    } catch (error) {
      dispatch({ type: 'LOAD_FAILURE', payload: error instanceof Error ? error.message : 'Failed to load book' });
    }
  };

  const setCurrentChapter = (chapterId: string) => {
    if (state.book) {
      const chapter = state.book.chapters.find(ch => ch.id === chapterId);
      if (chapter) {
        dispatch({ type: 'SET_CURRENT_CHAPTER', payload: chapter });
      }
    }
  };

  const setCurrentSection = (sectionId: string) => {
    if (state.book) {
      for (const chapter of state.book.chapters) {
        const section = chapter.sections.find(sec => sec.id === sectionId);
        if (section) {
          dispatch({ type: 'SET_CURRENT_SECTION', payload: section });
          dispatch({ type: 'SET_CURRENT_CHAPTER', payload: chapter });
          break;
        }
      }
    }
  };

  const toggleChapterExpansion = (chapterId: string) => {
    dispatch({ type: 'TOGGLE_CHAPTER_EXPANSION', payload: chapterId });
  };

  const toggleSectionExpansion = (sectionId: string) => {
    dispatch({ type: 'TOGGLE_SECTION_EXPANSION', payload: sectionId });
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Load book on mount
  useEffect(() => {
    loadBook();
  }, []);

  const value: BookContextType = {
    ...state,
    loadBook,
    setCurrentChapter,
    setCurrentSection,
    toggleChapterExpansion,
    toggleSectionExpansion,
    clearError,
  };

  return (
    <BookContext.Provider value={value}>
      {children}
    </BookContext.Provider>
  );
}

export function useBook() {
  const context = useContext(BookContext);
  if (context === undefined) {
    throw new Error('useBook must be used within a BookProvider');
  }
  return context;
}
